function initCollectionFiltersFloatingActions(filtersForm) {
	const host = filtersForm.querySelector('[data-collection-filters-actions-host]');
	const actions = filtersForm.querySelector('[data-collection-filters-actions]');
	if (!host || !actions) return;

	const module = filtersForm.closest('[mdle_name="module_7"]');
	const isModule7 = Boolean(module);
	const collectionProducts = filtersForm.closest('.collection-main-products');
	const scrollContainer = filtersForm.closest('.header-group-module-container');
	const portal = document.body;
	const desktopMedia = window.matchMedia('(min-width: 931px)');

	const getViewportHeight = () => window.visualViewport?.height ?? window.innerHeight;

	const getBottomOffset = () => (isModule7 ? 56 : 32);

	const isCollectionDesktopActive = () =>
		Boolean(collectionProducts) &&
		desktopMedia.matches &&
		collectionProducts.classList.contains('active');

	const isPanelVisible = () => {
		if (isModule7) return module.classList.contains('activate');
		if (collectionProducts) return isCollectionDesktopActive();
		return false;
	};

	const isPanelReady = () => {
		if (!isPanelVisible()) return false;

		const hostRect = host.getBoundingClientRect();
		if (hostRect.width < 1) return false;

		if (isModule7) {
			const moduleRect = module.getBoundingClientRect();
			return moduleRect.left > -20;
		}

		return hostRect.width >= 280;
	};

	const isBlockedByOverlay = () => {
		if (document.querySelector('.product-card-variant-modal[open]')) return true;

		const activeModules = document.querySelectorAll('.header-group-module.activate');

		if (isModule7) {
			for (const activeModule of activeModules) {
				if (activeModule !== module) return true;
			}
			return false;
		}

		for (const activeModule of activeModules) {
			if (activeModule.getAttribute('mdle_name') !== 'module_7') return true;
		}

		return false;
	};

	const clearFloatingStyles = () => {
		actions.style.position = '';
		actions.style.left = '';
		actions.style.width = '';
		actions.style.bottom = '';
		actions.style.zIndex = '';
		actions.style.removeProperty('--filters-actions-left');
		actions.style.removeProperty('--filters-actions-bottom');
		actions.classList.remove('is-entering');
		host.classList.remove('is-reserving');
	};

	const dockActions = () => {
		if (actions.parentElement !== host) {
			host.appendChild(actions);
		}
		actions.classList.remove('is-floating', 'is-entering');
		host.style.minHeight = '';
		clearFloatingStyles();
	};

	const positionFloatingActions = () => {
		const hostRect = host.getBoundingClientRect();
		const bottomOffset = getBottomOffset();
		const left = hostRect.left + Math.max(0, (hostRect.width - actions.offsetWidth) / 2);

		actions.style.position = 'fixed';
		actions.style.left = `${left}px`;
		actions.style.setProperty(
			'--filters-actions-bottom',
			isModule7 ? `calc(${bottomOffset}px + env(safe-area-inset-bottom, 0px))` : `${bottomOffset}px`
		);
		// module_7 panel = 15; collection content stays under header/panels/modal
		actions.style.zIndex = isModule7 ? '16' : '5';
	};

	const floatActions = () => {
		const wasFloating = actions.classList.contains('is-floating');

		if (actions.parentElement !== portal) {
			host.classList.add('is-reserving');
			host.style.minHeight = `${actions.offsetHeight + 24}px`;
			portal.appendChild(actions);
		}

		actions.classList.add('is-floating');

		if (!wasFloating) {
			actions.classList.add('is-entering');
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					actions.classList.remove('is-entering');
				});
			});
		}

		positionFloatingActions();
	};

	const update = () => {
		if (!isPanelReady() || isBlockedByOverlay()) {
			dockActions();
			return;
		}

		const bottomOffset = getBottomOffset();
		const actionsHeight = actions.offsetHeight || 76;
		const desiredTop = getViewportHeight() - bottomOffset - actionsHeight;
		const hostTop = host.getBoundingClientRect().top;
		const shouldFloat = hostTop > desiredTop;

		if (shouldFloat) {
			floatActions();
		} else {
			dockActions();
		}
	};

	let ticking = false;
	const scheduleUpdate = () => {
		if (ticking) return;
		ticking = true;
		requestAnimationFrame(() => {
			update();
			ticking = false;
		});
	};

	window.addEventListener('scroll', scheduleUpdate, { passive: true, capture: true });
	window.addEventListener('resize', scheduleUpdate, { passive: true });
	desktopMedia.addEventListener('change', scheduleUpdate);
	window.visualViewport?.addEventListener('resize', scheduleUpdate, { passive: true });
	window.visualViewport?.addEventListener('scroll', scheduleUpdate, { passive: true });
	scrollContainer?.addEventListener('scroll', scheduleUpdate, { passive: true });

	if (collectionProducts) {
		const panelObserver = new MutationObserver(scheduleUpdate);
		panelObserver.observe(collectionProducts, { attributes: true, attributeFilter: ['class'] });
		collectionProducts.addEventListener('transitionend', scheduleUpdate);
	}

	if (module) {
		const moduleObserver = new MutationObserver(scheduleUpdate);
		moduleObserver.observe(module, { attributes: true, attributeFilter: ['class'] });
		module.addEventListener('transitionend', scheduleUpdate);
	}

	const overlayObserver = new MutationObserver(scheduleUpdate);
	document.querySelectorAll('.header-group-module, .product-card-variant-modal').forEach((el) => {
		overlayObserver.observe(el, { attributes: true, attributeFilter: ['class', 'open'] });
	});

	if (typeof ResizeObserver !== 'undefined') {
		const resizeObserver = new ResizeObserver(scheduleUpdate);
		resizeObserver.observe(filtersForm);
		resizeObserver.observe(host);
	}

	scheduleUpdate();
	setTimeout(scheduleUpdate, 600);
}

function initCollectionFiltersForm(filtersForm) {
	const filterInputs = filtersForm.querySelectorAll('.collection-filter');
	const priceInputs = filtersForm.querySelectorAll('[data-collection-price-input]');
	const countEl = filtersForm.querySelector('[data-collection-filters-count]');
	const clearButton = filtersForm.querySelector('[data-collection-filters-clear]');
	const applyButton = filtersForm.querySelector('[data-collection-filters-apply]');
	const resultsUrl = filtersForm.dataset.resultsUrl || filtersForm.action;

	const updateFilterCount = () => {
		if (!countEl) return;
		const checkedCount = filtersForm.querySelectorAll('.collection-filter:checked').length;
		const hasPriceFilter = Array.from(priceInputs).some((input) => String(input.value || '').trim() !== '');
		countEl.textContent = String(checkedCount + (hasPriceFilter ? 1 : 0));
	};

	const applyFilters = (event) => {
		event.preventDefault();

		const formData = new FormData(filtersForm);
		const params = new URLSearchParams();
		for (const [key, value] of formData.entries()) {
			if (String(value).trim() === '') continue;
			params.set(key, value);
		}

		const baseUrl = new URL(resultsUrl, window.location.origin);
		params.forEach((value, key) => {
			baseUrl.searchParams.set(key, value);
		});
		window.location.href = baseUrl.toString();
	};

	const clearFilters = (event) => {
		event.preventDefault();
		window.location.href = resultsUrl;
	};

	filtersForm.addEventListener('submit', applyFilters);
	applyButton?.addEventListener('click', applyFilters);
	clearButton?.addEventListener('click', clearFilters);

	filterInputs.forEach((input) => {
		input.addEventListener('change', updateFilterCount);
	});

	priceInputs.forEach((input) => {
		input.addEventListener('input', updateFilterCount);
		input.addEventListener('change', updateFilterCount);
	});

	updateFilterCount();
	initCollectionFiltersFloatingActions(filtersForm);
}

class CollectionPriceRange extends HTMLElement {
	connectedCallback() {
		if (this.dataset.wired === 'true') return;
		this.dataset.wired = 'true';

		this.querySelectorAll('input').forEach((element) => {
			element.addEventListener('change', this.onRangeChange.bind(this));
			element.addEventListener('keydown', this.onKeyDown.bind(this));
		});
		this.setMinAndMaxValues();
	}

	onRangeChange(event) {
		this.adjustToValidValues(event.currentTarget);
		this.setMinAndMaxValues();
	}

	onKeyDown(event) {
		if (event.metaKey) return;

		const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
		if (!event.key.match(pattern)) event.preventDefault();
	}

	setMinAndMaxValues() {
		const inputs = this.querySelectorAll('input');
		const minInput = inputs[0];
		const maxInput = inputs[1];
		if (!minInput || !maxInput) return;

		if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
		if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
		if (minInput.value === '') maxInput.setAttribute('data-min', 0);
		if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
	}

	adjustToValidValues(input) {
		const value = Number(input.value);
		const min = Number(input.getAttribute('data-min'));
		const max = Number(input.getAttribute('data-max'));

		if (Number.isNaN(value)) return;
		if (value < min) input.value = min;
		if (value > max) input.value = max;
	}
}

if (!customElements.get('price-range')) {
	customElements.define('price-range', CollectionPriceRange);
}

document.addEventListener('DOMContentLoaded', () => {
	document.querySelectorAll('[data-collection-filters-form]').forEach(initCollectionFiltersForm);
});
